import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import TopNav from "@/components/TopNav";

function formatError(d) {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || JSON.stringify(d);
}

function GoogleButton({ label = "Continue with Google" }) {
  const click = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button onClick={click} className="w-full border border-stone-300 rounded-full py-3 font-semibold flex items-center justify-center gap-3 hover:bg-stone-50 transition" data-testid="google-signin-btn">
      <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.8 32.3 29.4 35.5 24 35.5c-6.4 0-11.6-5.2-11.6-11.5S17.6 12.5 24 12.5c3 0 5.7 1.1 7.8 3l5.7-5.7C33.9 6.5 29.2 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.3 19 13.5 24 13.5c3 0 5.7 1.1 7.8 3l5.7-5.7C33.9 7.5 29.2 5.5 24 5.5c-7.4 0-13.7 4.2-17.7 9.2z"/><path fill="#4CAF50" d="M24 43.5c5.1 0 9.7-1.9 13.2-5.1l-6.1-5c-2 1.4-4.4 2.1-7.1 2.1-5.3 0-9.8-3.2-11.3-7.7l-6.6 5.1C9.9 39.1 16.4 43.5 24 43.5z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.1 5C40.5 36.2 43.5 30.5 43.5 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
      {label}
    </button>
  );
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const data = await login(email, password);
      nav(loc.state?.from || (data?.role === "admin" ? "/admin" : "/dashboard"));
    } catch (e) {
      setErr(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const useDemo = () => { setEmail("josefa@tawbti.com"); setPassword("Josefa@2026"); };
  const useAdmin = () => { setEmail("admin@tawbti.com"); setPassword("Admin@Tawbti2026"); };

  return (
    <div className="min-h-screen">
      <TopNav/>
      <main className="max-w-md mx-auto px-6 py-16">
        <h1 className="tw-display text-4xl font-extrabold tracking-tight" data-testid="login-title">Welcome back.</h1>
        <p className="text-stone-600 mt-2">Sign in to continue your learning.</p>

        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div>
            <label className="tw-mono-label block mb-2">Email</label>
            <input className="tw-input" type="email" value={email} onChange={e=>setEmail(e.target.value)} required data-testid="login-email-input"/>
          </div>
          <div>
            <label className="tw-mono-label block mb-2">Password</label>
            <input className="tw-input" type="password" value={password} onChange={e=>setPassword(e.target.value)} required data-testid="login-password-input"/>
          </div>
          {err && <div className="text-red-600 text-sm" data-testid="login-error">{err}</div>}
          <button type="submit" disabled={busy} className="tw-btn-primary w-full" data-testid="login-submit-btn">{busy ? "Signing in..." : "Sign in"}</button>
        </form>

        <div className="my-6 flex items-center gap-3 text-stone-400 text-sm"><div className="h-px flex-1 bg-stone-200"/>or<div className="h-px flex-1 bg-stone-200"/></div>
        <GoogleButton/>

        <button onClick={useDemo} className="mt-6 text-sm text-stone-600 underline" data-testid="login-demo-btn">Use the demo learner account (Josefa)</button>

        <details className="mt-8 tw-card p-4 bg-stone-50/60" data-testid="demo-creds-card">
          <summary className="cursor-pointer text-sm font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-extrabold">i</span>
            Try TAWBTI — demo credentials
          </summary>
          <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
            <button onClick={useDemo} className="text-left p-3 rounded-lg border border-stone-200 hover:border-stone-900 transition bg-white" data-testid="demo-fill-learner">
              <div className="tw-mono-label" style={{color:"var(--tawbti-blue-text)"}}>LEARNER</div>
              <div className="font-mono text-[13px] mt-1">josefa@tawbti.com</div>
              <div className="font-mono text-[13px] text-stone-500">Josefa@2026</div>
              <div className="text-xs text-stone-500 mt-2">Block 1 complete, 91% quiz score — explore the lesson player + AI tutor</div>
            </button>
            <button onClick={useAdmin} className="text-left p-3 rounded-lg border border-stone-200 hover:border-stone-900 transition bg-white" data-testid="demo-fill-admin">
              <div className="tw-mono-label" style={{color:"var(--tawbti-green-text)"}}>ADMIN</div>
              <div className="font-mono text-[13px] mt-1">admin@tawbti.com</div>
              <div className="font-mono text-[13px] text-stone-500">Admin@Tawbti2026</div>
              <div className="text-xs text-stone-500 mt-2">Course Builder, AI Quiz Generator, Excel import</div>
            </button>
          </div>
          <div className="mt-3 text-[11px] text-stone-500">Click a card to fill the form, then press <strong>Sign in</strong>.</div>
        </details>

        <div className="mt-8 text-sm text-stone-600">
          New here? <Link to="/register" className="tw-link font-semibold" data-testid="login-go-register">Create an account</Link>
        </div>
      </main>
    </div>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await register(form.email, form.password, form.name);
      nav("/dashboard");
    } catch (e) { setErr(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen">
      <TopNav/>
      <main className="max-w-md mx-auto px-6 py-16">
        <h1 className="tw-display text-4xl font-extrabold tracking-tight" data-testid="register-title">Create your account.</h1>
        <p className="text-stone-600 mt-2">Start the One-Hour MBA framework.</p>
        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div><label className="tw-mono-label block mb-2">Full name</label><input className="tw-input" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} required data-testid="register-name-input"/></div>
          <div><label className="tw-mono-label block mb-2">Email</label><input className="tw-input" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} required data-testid="register-email-input"/></div>
          <div><label className="tw-mono-label block mb-2">Password</label><input className="tw-input" type="password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} required minLength={6} data-testid="register-password-input"/></div>
          {err && <div className="text-red-600 text-sm" data-testid="register-error">{err}</div>}
          <button type="submit" disabled={busy} className="tw-btn-primary w-full" data-testid="register-submit-btn">{busy?"Creating...":"Create account"}</button>
        </form>
        <div className="my-6 flex items-center gap-3 text-stone-400 text-sm"><div className="h-px flex-1 bg-stone-200"/>or<div className="h-px flex-1 bg-stone-200"/></div>
        <GoogleButton label="Sign up with Google"/>
        <div className="mt-8 text-sm text-stone-600">Have an account? <Link to="/login" className="tw-link font-semibold" data-testid="register-go-login">Sign in</Link></div>
      </main>
    </div>
  );
}
